import { BaseAction } from "../../../core/actions/BaseAction";
import { getPassengerDataFromLeg, getPassengerFromBooking } from "../../../Lib/Utils/booking";
import { convertISO8601ToDuration, convertTimeToArabic, getShortDateFromDateString, getShortDateFromDateStringFormatted, getTrainClassTypeBaseOnProduct } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { ProductTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { ILeg, ILegTrain, ISelectedTrainLegs } from "../../../Orchestrator/Types/Index";
import { IGetLegsFromTripActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/TrainActions";


export class GetLegsSummaryFromTripAction extends BaseAction<ISelectedTrainLegs | null, IGetLegsFromTripActionParams> {

    override async run(): Promise<ISelectedTrainLegs | null> {
        let result: ISelectedTrainLegs | null = null;
        try {
            const { trip, tripDirection, trainLegsValue, bookingData, bookedOffers = [] } = this.params!
            const legTrips: ILegTrain[] = [];
            const bookingPassengerList = getPassengerFromBooking(bookingData?.passengers || []);            
            const getLegPriceClass = (legId: string): { trainClass: string; productName: string } => {
                const offersForLeg = (bookedOffers || []).filter(
                    (offer: any) => offer?.tripCoverage?.coveredLegIds?.includes(legId)
                );
                if (offersForLeg.length === 0) {
                    return { trainClass: trip?.priceClass || "", productName: "" };
                }

                const isShuttleOffer = (offer: any): boolean =>
                    (offer?.products || []).some((product: any) => {
                        const code = String(product?.code || "").toLowerCase();
                        const summary = String(product?.summary || "").toLowerCase();
                        return code === "sht" || summary.includes("shuttle");
                    });

                const hasStatus = (offer: any, statuses: string[]): boolean =>
                    (offer?.admissions || []).some((admission: any) =>
                        statuses.includes(String(admission?.status || ""))
                    );

                const fulfilledOffers = offersForLeg.filter((offer: any) =>
                    hasStatus(offer, ["FULFILLED"])
                );
                const activeOffers = offersForLeg.filter((offer: any) =>
                    hasStatus(offer, ["FULFILLED", "REFUNDED", "RELEASED", "ON_HOLD", "CANCELLED"])
                );

                const candidateOffers =
                    fulfilledOffers.length > 0
                        ? fulfilledOffers
                        : activeOffers.length > 0
                            ? activeOffers
                            : offersForLeg;
                const primaryOffer =
                    candidateOffers.find((offer: any) => !isShuttleOffer(offer)) ||
                    candidateOffers[0];

                const trainClass = getTrainClassTypeBaseOnProduct(primaryOffer?.products || []) || trip?.priceClass || "";
                const admissionProduct = primaryOffer?.products?.find((p: any) => p.type === ProductTypeEnum.ADMISSION_POINT2POINT) || primaryOffer?.products?.[0];
                const productName = admissionProduct?.summary || admissionProduct?.description || "";
                return { trainClass, productName };
            };

            (trip?.legs || []).forEach((leg: ILeg) => {
                //Get leg offer
                const isTrain = leg?.timedLeg?.service?.mode?.ptMode == "TRAIN";
                const legPriceInfo = isTrain ? getLegPriceClass(leg.id) : { trainClass: "", productName: "" };
                const legTripDetails: ILegTrain = {
                    legId: leg.id,
                    origin: leg.timedLeg?.start?.stopPlaceRef || "",
                    destination: leg.timedLeg?.end?.stopPlaceRef || "",
                    fromName: leg?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef || "",
                    toName: leg?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef || "",
                    fromDisplayName: leg?.timedLeg?.start?.stopPlaceName || "",
                    toDisplayName: leg?.timedLeg?.end?.stopPlaceName || "",
                    trainType: isTrain ? "train" : "shuttle",
                    duration: convertISO8601ToDuration(leg?.timedLeg?.duration || ""),
                    startTime: leg?.timedLeg?.start?.serviceDeparture?.timetabledTime || "",
                    endTime: leg?.timedLeg?.end?.serviceArrival?.timetabledTime || "",
                    shortStartDate: getShortDateFromDateString(leg?.timedLeg?.start?.serviceDeparture?.timetabledTime || ""),
                    shortEndDate: getShortDateFromDateString(leg?.timedLeg?.end?.serviceDeparture?.timetabledTime || ""),
                    departureTime: convertTimeToArabic(leg?.timedLeg?.start?.serviceDeparture?.timetabledTime || ""),
                    arrivalTime: convertTimeToArabic(leg?.timedLeg?.end?.serviceArrival?.timetabledTime || ""),
                    stops: [],// trip.transfers > 0 ? Array(trip.transfers).fill('Stop') : [],
                    transfers: 0,// trip.transfers,
                    trainNo: `${leg?.timedLeg?.service?.vehicleNumbers?.[0] || ''}`,
                    trainClass: legPriceInfo.trainClass,
                    productName: legPriceInfo.productName,
                    passengers: getPassengerDataFromLeg(bookingPassengerList, leg.id, bookingData),  //[],// tripPassenger?.outbound || []
                    shortStartDateFormat: leg?.timedLeg?.start?.serviceDeparture?.timetabledTime,
                    shortEndDateFormat: leg?.timedLeg?.end?.serviceDeparture?.timetabledTime,
                }
                legTrips.push(legTripDetails)
            })

            const updatedSelectedTrainLegs: ISelectedTrainLegs = {
                ...trainLegsValue,
                [tripDirection]: legTrips
            };
 

            updateRecoilState("selectedTrainLegsAtom", updatedSelectedTrainLegs)
            result = updatedSelectedTrainLegs // Return the object on success
        } catch (error) {
            console.error("Error in GetLegsSummaryFromTripAction:", error);
            result = null; // Ensure null is returned on error
        } finally {
            return result
        }
    }
}

// Create a singleton instance
export const getLegsSummaryFromTripAction = new GetLegsSummaryFromTripAction(); 
